import { useRuntimeVariables } from 'app-kit'
import { useGeckoAuth } from 'features/auth'
import {
  applyChatHistoryBulkAction,
  type ChatHistoryBulkAction,
  type ChatHistoryBulkActionHandlers,
} from 'features/embeddedSidebar/sessions/chatHistoryBulkActions'
import { useGeckoPermissions } from 'features/permissions'
import { useWorkspaceTabs } from 'features/workspaceTabs/contexts/WorkspaceTabsContext'
import { useCallback, useRef } from 'react'
import type { RolodexLoadedEntryJson } from 'types'

import type { RecordedAgenticChatSession } from './useAgenticChatSessionHistory'

export function useChatHistoryBulkActions({
  onArchiveSessions,
  onDeleteSessions,
  onUnarchiveSessions,
  onRemoveLocalSession,
  sessions,
  siteId,
}: ChatHistoryBulkActionHandlers & {
  sessions: RecordedAgenticChatSession[]
  siteId: string | undefined
}) {
  const { authorizedAxios } = useGeckoAuth()
  const { ROLODEX_URL } = useRuntimeVariables()
  const { user } = useGeckoPermissions()
  const userId = user?.id
  const { closeWorkspaceTab, openTabs } = useWorkspaceTabs()
  const current = useRef({ sessions, openTabs })
  current.current = { sessions, openTabs }
  const inFlight = useRef(false)

  return useCallback(
    async (action: ChatHistoryBulkAction): Promise<boolean> => {
      if (!siteId || !userId || inFlight.current) return false
      inFlight.current = true
      try {
        return await applyChatHistoryBulkAction({
          action,
          closeWorkspaceTab,
          getCurrentState: () => current.current,
          loadEntries: async (query) => {
            const response = await authorizedAxios.post<
              RolodexLoadedEntryJson[]
            >(`${ROLODEX_URL}/api/v2/entries/load`, query)
            return response.data
          },
          siteId,
          userId,
          onArchiveSessions,
          onDeleteSessions,
          onUnarchiveSessions,
          onRemoveLocalSession,
        })
      } catch {
        return false
      } finally {
        inFlight.current = false
      }
    },
    [
      ROLODEX_URL,
      authorizedAxios,
      closeWorkspaceTab,
      onArchiveSessions,
      onDeleteSessions,
      onUnarchiveSessions,
      onRemoveLocalSession,
      siteId,
      userId,
    ]
  )
}
